import React from 'react';

import {
    Backdrop,
    Heading,
    Input,
    Label,
    Schedule,
    Surface,
    Switch,
    Text
} from '@/components/ui';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'holiday'];
const SLOTS_PER_DAY = 24 * 4;

// AlarmSchedule — alarm-domain composition around the generic
// `<Schedule>` primitive. Adds the min/max value inputs, the unit
// display, the per-section "Change?" toggles used in bulk-edit, and
// the baseline-alarm overlay message. Owns the conversion between the
// backend's stringly-typed schedule shape (`{ monday: ['on', 'off',
// …] }`) and Schedule's boolean shape, so callers can keep working
// with whichever shape the wire/store gives them.
//
// Modes:
//   - Display-only: omit all on*Change handlers. Grid and inputs
//     render as read-only.
//   - Editable: pass on*Change handlers. Grid is drag-paintable, min
//     and max are <Input>s.
//   - Bulk-edit: also pass showBulkEditToggles + the four enabled/
//     onEnabledChange props. The sidebar renders the "Change?" Switch
//     for each section; toggling it off greys the section out.
//   - Baseline (or any other "this is auto-generated, don't touch")
//     case: pass disabledMessage. Renders a centered Backdrop scrim
//     with the message on top of everything.

const SCHEDULE_STRING_TO_BOOLEAN = stringSchedule => {
    if (!stringSchedule || Object.keys(stringSchedule).length === 0) {
        return DAYS.reduce((acc, day) => {
            acc[day] = Array(SLOTS_PER_DAY).fill(false);

            return acc;
        }, {});
    }

    return DAYS.reduce((acc, day) => {
        const dayArray = stringSchedule[day];

        acc[day] = Array.from({ length: SLOTS_PER_DAY }, (_, i) => dayArray?.[i] === 'on');

        return acc;
    }, {});
};

const SCHEDULE_BOOLEAN_TO_STRING = booleanSchedule => DAYS.reduce((acc, day) => {
    acc[day] = (booleanSchedule[day] || Array(SLOTS_PER_DAY).fill(false)).map(b => (b ? 'on' : 'off'));

    return acc;
}, {});

const parseNumber = value => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const parsed = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(parsed) ? parsed : null;
};

export function AlarmSchedule({
    disabledMessage,
    maxValue,
    minValue,
    onMaxValueChange,
    onMinValueChange,
    onScheduleChange,
    onScheduleEnabledChange,
    onValuesEnabledChange,
    schedule,
    scheduleEnabled = true,
    showBulkEditToggles = false,
    unit = '',
    valuesEnabled = true
}) {
    const minInputId = React.useId();
    const maxInputId = React.useId();

    const isLocked = Boolean(disabledMessage);
    const effectiveScheduleEnabled = !isLocked && scheduleEnabled;
    const effectiveValuesEnabled = !isLocked && valuesEnabled;
    const isScheduleEditable = effectiveScheduleEnabled && typeof onScheduleChange === 'function';
    const isValuesEditable = effectiveValuesEnabled && typeof onMinValueChange === 'function' && typeof onMaxValueChange === 'function';

    // When the schedule section is disabled (bulk-edit "don't change")
    // we paint an empty grid even though the underlying schedule may
    // hold values — same UX the previous Schedule had.
    const booleanSchedule = SCHEDULE_STRING_TO_BOOLEAN(effectiveScheduleEnabled ? schedule : null);

    const handleScheduleChange = nextBoolean => {
        onScheduleChange?.(SCHEDULE_BOOLEAN_TO_STRING(nextBoolean));
    };

    const parsedMin = parseNumber(minValue);
    const parsedMax = parseNumber(maxValue);
    const valuesError = (parsedMin !== null && parsedMax !== null && parsedMax < parsedMin)
        ? i18next.t('schedule.validation.max_below_min', 'Max must be greater than or equal to Min')
        : null;

    return (
        // Two-layer wrapper: the outer relative box defines the area the
        // Backdrop will fill; the inner flex box carries the form columns
        // and pulls them in via `tw-p-2`, so the scrim ends up visibly
        // larger than the form itself without touching the Backdrop
        // primitive.
        <div className="tw-relative tw-flex-1 tw-w-full">
            {disabledMessage && (
                <Backdrop>
                    <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-p-8">
                        <Surface tone="neutral">
                            <Text variant="body">{disabledMessage}</Text>
                        </Surface>
                    </div>
                </Backdrop>
            )}

            <div className="tw-flex tw-h-full tw-w-full tw-gap-4 tw-p-5">
                <div className="tw-w-65pct">
                    <Schedule
                        onChange={isScheduleEditable ? handleScheduleChange : undefined}
                        readonly={!isScheduleEditable}
                        value={booleanSchedule}
                    />
                </div>

                <div className="tw-w-35pct tw-flex-none tw-flex tw-flex-col tw-gap-4">
                    {/* Bulk-edit "Change schedule?" toggle */}
                    {showBulkEditToggles && onScheduleEnabledChange && (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <div className="tw-flex tw-justify-between tw-items-center">
                                <Heading as="h3" variant="title-sm">{i18next.t('glossary.schedule').capitalize()}</Heading>
                                <Switch
                                    checked={scheduleEnabled}
                                    disabled={isLocked}
                                    label={i18next.t('common.change', 'Change?')}
                                    onCheckedChange={onScheduleEnabledChange}
                                />
                            </div>
                            {!effectiveScheduleEnabled && (
                                <Text tone="muted" variant="caption">
                                    {i18next.t(
                                        'alarms.schedule_change_hint',
                                        "You won't be able to change the schedule before you enable changes."
                                    )}
                                </Text>
                            )}
                        </div>
                    )}

                    {/* Values section */}
                    <div className="tw-flex tw-flex-col tw-gap-2">
                        <div className="tw-flex tw-justify-between tw-items-center">
                            <Heading as="h3" variant="title-sm">{i18next.t('common.values').capitalize()}</Heading>
                            {showBulkEditToggles && onValuesEnabledChange && (
                                <Switch
                                    checked={valuesEnabled}
                                    disabled={isLocked}
                                    label={i18next.t('common.change', 'Change?')}
                                    onCheckedChange={onValuesEnabledChange}
                                />
                            )}
                        </div>

                        <Surface tone="neutral">
                            <div className="tw-flex tw-gap-3">
                                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-1">
                                    <Label disabled={!effectiveValuesEnabled} htmlFor={minInputId}>
                                        {`Min${unit ? ` (${unit})` : ''}`}
                                    </Label>
                                    {isValuesEditable ? (
                                        <Input
                                            disabled={!effectiveValuesEnabled}
                                            id={minInputId}
                                            onChange={event => onMinValueChange(event.target.value)}
                                            type="number"
                                            value={effectiveValuesEnabled ? (minValue ?? '') : ''}
                                        />
                                    ) : (
                                        <Text variant="body">{minValue ?? '—'}</Text>
                                    )}
                                </div>
                                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-1">
                                    <Label disabled={!effectiveValuesEnabled} htmlFor={maxInputId}>
                                        {`Max${unit ? ` (${unit})` : ''}`}
                                    </Label>
                                    {isValuesEditable ? (
                                        <Input
                                            disabled={!effectiveValuesEnabled}
                                            id={maxInputId}
                                            onChange={event => onMaxValueChange(event.target.value)}
                                            type="number"
                                            value={effectiveValuesEnabled ? (maxValue ?? '') : ''}
                                        />
                                    ) : (
                                        <Text variant="body">{maxValue ?? '—'}</Text>
                                    )}
                                </div>
                            </div>
                        </Surface>

                        {valuesError && effectiveValuesEnabled && (
                            <Text tone="destructive" variant="control">{valuesError}</Text>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
